import { withDependencies } from '@wix/thunderbolt-ioc'
import type {
	ExternalComponentFetchApi,
	IExternalComponentCssApplier,
	IExternalComponentCssManager,
	IExternalComponentWixCodeUrlGenerator,
} from '../types'
import {
	ExternalComponentCssApplierSymbol,
	ExternalComponentFetchSymbol,
	ExternalComponentWixCodeUrlGeneratorSymbol,
} from '../symbols'

export const BYOC_ROOT_CLASS = 'byoc-root'

type cssFile = {
	content: Promise<string>
	users: Set<string>
	cacheKillerCounter?: number
}

export const ExternalComponentCssManager = withDependencies<IExternalComponentCssManager>(
	[ExternalComponentFetchSymbol, ExternalComponentWixCodeUrlGeneratorSymbol, ExternalComponentCssApplierSymbol],
	(
		fetchApi: ExternalComponentFetchApi,
		externalComponentWixCodeUrlGenerator: IExternalComponentWixCodeUrlGenerator,
		externalComponentCssApplier: IExternalComponentCssApplier
	) => {
		const cssFiles: Record<string, cssFile> = {}
		Object.entries(externalComponentCssApplier.getLoadedModules()).forEach(([url, css]) => {
			cssFiles[url] = {
				content: Promise.resolve(css),
				users: new Set(),
			}
		})

		const fetchCssFile = async (url: string, cacheKillerCounter?: number): Promise<string> => {
			const fullUrl = externalComponentWixCodeUrlGenerator.generateUrl({
				componentRelativeUrl: url,
				fileType: 'css',
				cacheKillerCounter,
			})
			return fetchApi(fullUrl, false)
				.then((res: Response) => res.text())
				.catch(() => {
					return ''
				})
		}

		return {
			async use(currentPageId, cssUrl) {
				const alreadyLoaded = cssUrl in cssFiles
				if (!alreadyLoaded) {
					cssFiles[cssUrl] = {
						content: fetchCssFile(cssUrl),
						users: new Set(),
					}
				}
				const isFirstUse = cssFiles[cssUrl].users.size === 0
				if (isFirstUse) {
					externalComponentCssApplier.add(cssUrl, await cssFiles[cssUrl].content)
				} else {
					await cssFiles[cssUrl].content // resolve only when the css file is loaded
				}
				cssFiles[cssUrl].users.add(currentPageId)
			},
			release(currentPageId, cssUrl) {
				cssFiles[cssUrl].users.delete(currentPageId)
				const noLongerUsed = cssFiles[cssUrl].users.size === 0
				if (noLongerUsed) {
					externalComponentCssApplier.remove(cssUrl)
				}
			},
			releaseAllByPage(currentPageId) {
				Object.keys(cssFiles).forEach((cssUrl) => {
					this.release(currentPageId, cssUrl)
				})
			},
			async forceReload(cssUrl: string) {
				if (!(cssUrl in cssFiles)) {
					cssFiles[cssUrl] = {
						content: fetchCssFile(cssUrl),
						users: new Set(),
					}
				} else {
					const cacheKiller = (cssFiles[cssUrl].cacheKillerCounter || 0) + 1
					cssFiles[cssUrl].cacheKillerCounter = cacheKiller
					cssFiles[cssUrl].content = fetchCssFile(cssUrl, cacheKiller)
				}
				externalComponentCssApplier.update(cssUrl, await cssFiles[cssUrl].content)
			},

			async forceReloadAll() {
				Object.keys(cssFiles).forEach((cssUrl) => {
					this.forceReload(cssUrl)
				})
			},
		}
	}
)
